import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import styles from './TimelogView.module.css'
import apiClient from '../utils/api'
import ProjectDropdown from './ProjectDropdown'
import DatePickerComponent from './DatePickerComponent'
import DescriptionAutofill from './DescriptionAutofill'
import ProjectTaskSelectionModal from './modals/ProjectTaskSelectionModal'
import WorklogDescription from './WorklogDescription'
import {
    formatWorklogDate,
    formatWeekRange,
    formatDuration,
    formatDateForBackend,
    formatDisplayDate,
    getCurrentTimeFormatted,
    getWeekStart,
    moment,
    isToday,
    isSameDay
} from '../utils/dateUtils'
import { formatTime12Hour, formatTimeRange, formatTime24Hour } from '../utils/dateUtilsV2'
import { getUserTimezone } from '../utils/timezoneUtils'
import { parseMentions } from '../utils/mentionParser'

export default function TimelogView({ apiToken, user, availableProjects = [], availableTasks = [], onRefreshNeeded }) {
    // Get user's timezone for consistent time display - prioritize user profile timezone
    // Use useMemo to recalculate when user.timezone changes
    const userTimezone = useMemo(() => getUserTimezone(user?.timezone), [user?.timezone])

    const [isManualEntry, setIsManualEntry] = useState(false)
    const [description, setDescription] = useState('')
    const [selectedProject, setSelectedProject] = useState(null)
    const [selectedTask, setSelectedTask] = useState(null)
    const [currentTime, setCurrentTime] = useState('00:00:00')
    const [startTime, setStartTime] = useState('00:00')
    const [endTime, setEndTime] = useState('00:00')
    const [selectedDate, setSelectedDate] = useState(moment().toDate())
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [isBillable, setIsBillable] = useState(false)
    const [isTimerRunning, setIsTimerRunning] = useState(false)
    const [timerStartTime, setTimerStartTime] = useState(null)
    const [elapsedTime, setElapsedTime] = useState('00:00:00')

    // API data states
    const [userData, setUserData] = useState(null)
    const [recentWorklogs, setRecentWorklogs] = useState([])
    const [userProjects, setUserProjects] = useState([])
    const [userTasks, setUserTasks] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState(null)
    const [hasMoreWorklogs, setHasMoreWorklogs] = useState(true)

    // Infinite scroll state
    const [currentPage, setCurrentPage] = useState(1)
    const [timeTotals, setTimeTotals] = useState({ daily_totals: {}, weekly_totals: {} })

    // Pagination states for projects and tasks (keeping pagination for these)
    const [paginationParams, setPaginationParams] = useState({
        projects_page: 1,
        projects_per_page: 25,
        tasks_page: 1,
        tasks_per_page: 25,
        worklogs_per_page: 25 // Still used for infinite scroll page size
    })
    const [projectsPagination, setProjectsPagination] = useState(null)
    const [tasksPagination, setTasksPagination] = useState(null)
    const [timerError, setTimerError] = useState(null)
    const [timerSuccess, setTimerSuccess] = useState(null)
    const [openDropdownId, setOpenDropdownId] = useState(null)
    const [dropdownPosition, setDropdownPosition] = useState(null)
    const [editingWorklogId, setEditingWorklogId] = useState(null)
    const [worklogUpdates, setWorklogUpdates] = useState({})
    const [editingValues, setEditingValues] = useState({})
    const [entryDatePickers, setEntryDatePickers] = useState({})
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)
    const [showSplitModal, setShowSplitModal] = useState(false)
    const [splitEntry, setSplitEntry] = useState(null)
    const [splitTime, setSplitTime] = useState(0)
    const [isProjectSelectionModalOpen, setIsProjectSelectionModalOpen] = useState(false)
    const [pendingTimerData, setPendingTimerData] = useState(null)
    const [teammates, setTeammates] = useState([])

    // Intersection observer for infinite scroll
    const observer = useRef()

    // Refs for focus management
    const projectButtonRef = useRef(null)
    const startButtonRef = useRef(null)

    // Ref for description update timeout cleanup
    const descriptionTimeoutRef = useRef(null)

    // Cleanup description timeout on unmount
    useEffect(() => {
        return () => {
            if (descriptionTimeoutRef.current) {
                clearTimeout(descriptionTimeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const loadTeammates = async () => {
            if (!apiToken) return
            try {
                apiClient.setAuthToken(apiToken)
                // Use workspace users endpoint - available to all users (not admin-only)
                const response = await apiClient.get('/workspaces/users')
                setTeammates(response.users || [])
            } catch (err) {
                console.error('Failed to fetch workspace users:', err)
                setTeammates([])
            }
        }
        loadTeammates()
    }, [apiToken])

    useEffect(() => {
        if (userData && teammates.length === 0) {
            setTeammates([userData])
        }
    }, [userData, teammates.length])

    const mentionableUsernames = useMemo(() => {
        const usernames = new Set()
        teammates.forEach(user => {
            if (user?.username) {
                usernames.add(user.username)
            }
        })
        if (userData?.username) {
            usernames.add(userData.username)
        }
        recentWorklogs?.forEach(entry => {
            if (entry.user?.username) {
                usernames.add(entry.user.username)
            }
        })
        return Array.from(usernames)
    }, [teammates, userData, recentWorklogs])

    // Memoized split modal calculations - compute once instead of per-render
    const splitModalData = useMemo(() => {
        if (!splitEntry?.originalWorklog?.start_time || !splitEntry?.originalWorklog?.end_time) {
            return {
                isValid: false,
                startTimeFormatted: '--:--',
                endTimeFormatted: '--:--',
                splitTimeFormatted: '--:--',
                maxDuration: 100,
                firstDuration: '00:00:00',
                secondDuration: '00:00:00'
            }
        }

        const startMoment = moment(splitEntry.originalWorklog.start_time)
        const endMoment = moment(splitEntry.originalWorklog.end_time)

        if (!startMoment.isValid() || !endMoment.isValid()) {
            return {
                isValid: false,
                startTimeFormatted: '--:--',
                endTimeFormatted: '--:--',
                splitTimeFormatted: '--:--',
                maxDuration: 100,
                firstDuration: '00:00:00',
                secondDuration: '00:00:00'
            }
        }

        const totalDuration = endMoment.diff(startMoment)
        const splitDateTime = startMoment.clone().add(splitTime, 'milliseconds').toDate()
        const remainingDuration = totalDuration - splitTime

        return {
            isValid: true,
            startTimeFormatted: formatTime12Hour(startMoment.toDate(), userTimezone),
            endTimeFormatted: formatTime12Hour(endMoment.toDate(), userTimezone),
            splitTimeFormatted: formatTime12Hour(splitDateTime, userTimezone),
            maxDuration: totalDuration,
            firstDuration: formatDuration(splitTime / 1000),
            secondDuration: formatDuration(remainingDuration / 1000)
        }
    }, [splitEntry, splitTime, userTimezone])

    // Function to load initial user data and first page of worklogs
    const loadUserData = async (customPaginationParams = null) => {
        if (!apiToken) return

        try {
            setLoading(true)
            setCurrentPage(1)
            setRecentWorklogs([]) // Reset worklogs for fresh load

            // Set the API token for the client
            apiClient.setAuthToken(apiToken)

            // Use custom pagination params if provided, otherwise use current state
            const params = customPaginationParams || { ...paginationParams, worklogs_page: 1 }
            const data = await apiClient.getMe(params)

            setUserData(data.user)

            // Handle worklogs with infinite scroll support
            if (data.worklogs?.data) {
                setRecentWorklogs(data.worklogs.data)
                setHasMoreWorklogs(data.worklogs.pagination?.has_next_page || false)
                setCurrentPage(2) // Next page to load

                // Set time totals from backend
                if (data.worklogs.time_totals) {
                    setTimeTotals(data.worklogs.time_totals)
                }
            } else {
                // Fallback for old non-paginated response
                setRecentWorklogs(data.recent_worklogs || [])
                setHasMoreWorklogs(false)
            }

            if (data.projects?.data) {
                setUserProjects(data.projects.data)
                setProjectsPagination(data.projects.pagination)
            } else {
                // Fallback for old non-paginated response
                setUserProjects(data.projects || [])
                setProjectsPagination(null)
            }

            if (data.tasks?.data) {
                setUserTasks(data.tasks.data)
                setTasksPagination(data.tasks.pagination)
            } else {
                // Fallback for old non-paginated response
                setUserTasks(data.tasks || [])
                setTasksPagination(null)
            }

            setError(null)
        } catch (err) {
            console.error('Failed to load user data:', err)
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    // Function to load more worklogs (infinite scroll)
    const loadMoreWorklogs = async () => {
        if (!apiToken || !userData || loadingMore || !hasMoreWorklogs) return

        try {
            setLoadingMore(true)

            const params = { ...paginationParams, worklogs_page: currentPage }
            const data = await apiClient.getMe(params)

            if (data.worklogs?.data) {
                setRecentWorklogs(prev => [...prev, ...data.worklogs.data])
                setHasMoreWorklogs(data.worklogs.pagination?.has_next_page || false)
                setCurrentPage(prev => prev + 1)

                // Merge time totals from backend
                if (data.worklogs.time_totals) {
                    setTimeTotals(prev => ({
                        daily_totals: { ...prev.daily_totals, ...data.worklogs.time_totals.daily_totals },
                        weekly_totals: { ...prev.weekly_totals, ...data.worklogs.time_totals.weekly_totals }
                    }))
                }
            }
        } catch (err) {
            console.error('Failed to load more worklogs:', err)
        } finally {
            setLoadingMore(false)
        }
    }

    // Function to refresh just the worklogs
    const refreshWorklogs = async () => {
        if (!apiToken || !userData) return

        try {
            // Reset to first page and reload
            setCurrentPage(1)
            setRecentWorklogs([])

            const params = { ...paginationParams, worklogs_page: 1 }
            const data = await apiClient.getMe(params)

            if (data.worklogs?.data) {
                setRecentWorklogs(data.worklogs.data)
                setHasMoreWorklogs(data.worklogs.pagination?.has_next_page || false)
                setCurrentPage(2)

                // Set time totals from backend
                if (data.worklogs.time_totals) {
                    setTimeTotals(data.worklogs.time_totals)
                }
            } else {
                // Fallback for old non-paginated response
                setRecentWorklogs(data.recent_worklogs || [])
                setHasMoreWorklogs(false)
            }
        } catch (err) {
            console.error('Failed to refresh worklogs:', err)
        }
    }

    // Intersection observer callback for infinite scroll
    const lastWorklogElementRef = useCallback(node => {
        if (loading || loadingMore) return
        if (observer.current) observer.current.disconnect()
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMoreWorklogs) {
                loadMoreWorklogs()
            }
        }, {
            threshold: 0.1,
            rootMargin: '100px'
        })
        if (node) observer.current.observe(node)
    }, [loading, loadingMore, hasMoreWorklogs])

    // Cleanup IntersectionObserver on unmount
    useEffect(() => {
        return () => {
            if (observer.current) {
                observer.current.disconnect()
            }
        }
    }, [])

    // Load user data and related information on component mount
    useEffect(() => {
        loadUserData()
    }, [apiToken])

    // Expose refresh function to parent component
    useEffect(() => {
        if (onRefreshNeeded) {
            onRefreshNeeded(() => refreshWorklogs)
        }
    }, [onRefreshNeeded]) // Remove refreshWorklogs from deps to prevent infinite loop

    // Check for worklog to scroll to after page reload
    useEffect(() => {
        if (!loading && recentWorklogs.length > 0) {
            const scrollToWorklogId = sessionStorage.getItem('scrollToWorklogId')
            if (scrollToWorklogId) {
                console.log('🔄 Page reloaded, checking for worklog to scroll to:', scrollToWorklogId)

                // Clear the stored ID
                sessionStorage.removeItem('scrollToWorklogId')

                // Scroll to the worklog after a short delay to ensure DOM is rendered
                setTimeout(() => {
                    scrollToWorklog(scrollToWorklogId)
                }, 1000)
            }
        }
    }, [loading, recentWorklogs])

    // Check for running worklog from recentWorklogs data
    useEffect(() => {
        const checkForRunningWorklog = () => {
            if (!userData || !recentWorklogs) return

            // Look for running worklog in recent worklogs data
            const runningWorklog = recentWorklogs.find(worklog => worklog.status === 'running')

            if (runningWorklog) {
                console.log('Found running worklog:', runningWorklog)

                // Set timer as running
                setIsTimerRunning(true)
                const startTime = moment(runningWorklog.start_time).toDate()
                setTimerStartTime(startTime)

                // Set form values from running worklog
                setDescription(runningWorklog.description || '')

                // Set billable status from running worklog
                setIsBillable(runningWorklog.billable || false)

                // Set project/task if available - match with user's projects/tasks
                if (runningWorklog.project_id && userProjects.length > 0) {
                    const matchedProject = userProjects.find(p => p.id === runningWorklog.project_id)
                    if (matchedProject) {
                        console.log('Matched running worklog project:', matchedProject)
                        setSelectedProject(matchedProject)
                    }
                }
                if (runningWorklog.task_id && userTasks.length > 0) {
                    const matchedTask = userTasks.find(t => t.id === runningWorklog.task_id)
                    if (matchedTask) {
                        console.log('Matched running worklog task:', matchedTask)
                        setSelectedTask(matchedTask)
                    }
                }
            } else {
                // No running worklog found, ensure timer is not running
                if (isTimerRunning) {
                    console.log('No running worklog found, stopping timer')
                    setIsTimerRunning(false)
                    setTimerStartTime(null)
                    setElapsedTime('00:00:00')
                }
            }
        }

        checkForRunningWorklog()
    }, [userData, recentWorklogs, userProjects, userTasks])

    // Helper function for elapsed time formatting (keeping for timer functionality)
    const formatElapsedTime = (seconds) => {
        return formatDuration(seconds)
    }

    // Process recent worklogs into grouped format using backend time totals
    const processWorklogsData = (worklogs) => {
        if (!worklogs || worklogs.length === 0) return {}

        // Filter out running worklogs - they should not appear in the dashboard
        const completedWorklogs = worklogs.filter(worklog => worklog.status !== 'running')

        const groupedData = {}

        completedWorklogs.forEach(worklog => {
            const date = moment(worklog.start_time).toDate()
            const dateKey = formatDateForBackend(worklog.start_time)
            const displayDate = formatWorklogDate(worklog.start_time)

            // Get Monday of the week for this date
            const weekStartDate = getWeekStart(date)
            const weekKey = formatWeekRange(weekStartDate)

            if (!groupedData[weekKey]) {
                // Use backend weekly total if available, otherwise fallback to 0
                const backendWeekTotal = timeTotals.weekly_totals[weekKey] || '00:00:00'
                groupedData[weekKey] = {
                    weekTotal: backendWeekTotal,
                    weekStartDate: weekStartDate,
                    dates: {}
                }
            }

            if (!groupedData[weekKey].dates[dateKey]) {
                // Use backend daily total if available, otherwise fallback to 0
                const startDateKey = worklog.start_time.split('T')[0] // Extract date part from start_time
                const backendDayTotal = timeTotals.daily_totals[startDateKey] || '00:00:00'
                groupedData[weekKey].dates[dateKey] = {
                    dayTotal: backendDayTotal,
                    displayDate: displayDate,
                    entries: []
                }
            }

            groupedData[weekKey].dates[dateKey].entries.push({
                id: worklog.id,
                description: worklog.description || '<no description>',
                project: worklog.project_name || worklog.workable_name || 'No project',
                timeRange: formatTimeRange(worklog.start_time, worklog.end_time, userTimezone),
                duration: formatDuration(worklog.duration_seconds),
                durationSeconds: worklog.duration_seconds || 0,
                billable: worklog.billable || false,
                tags: [], // Tags would need to be added to the API response
                // Preserve original worklog data for duplication
                originalWorklog: worklog
            })
        })

        // Sort weeks by date (most recent first)
        const sortedGroupedData = {}
        const sortedWeeks = Object.keys(groupedData).sort((a, b) => {
            return groupedData[b].weekStartDate - groupedData[a].weekStartDate
        })

        sortedWeeks.forEach(weekKey => {
            const weekData = groupedData[weekKey]

            // Sort dates within each week (most recent first)
            const sortedDates = {}

            const sortedDateKeys = Object.keys(weekData.dates).sort((a, b) => {
                const dateA = moment(a).toDate()
                const dateB = moment(b).toDate()
                return dateB - dateA // Most recent first
            })

            sortedDateKeys.forEach(dateKey => {
                const dateData = weekData.dates[dateKey]

                // Sort entries within each date (most recent first by start_time)
                const sortedEntries = [...dateData.entries].sort((a, b) => {
                    const startA = moment(a.originalWorklog?.start_time || 0).toDate()
                    const startB = moment(b.originalWorklog?.start_time || 0).toDate()
                    return startB - startA // Most recent first
                })

                // Always use the LATEST backend daily total from timeTotals
                // Derive the YYYY-MM-DD key from the first entry's start_time
                let latestDayTotal = dateData.dayTotal
                if (sortedEntries.length > 0 && sortedEntries[0].originalWorklog?.start_time) {
                    const startDateKey = sortedEntries[0].originalWorklog.start_time.split('T')[0]
                    latestDayTotal = timeTotals.daily_totals[startDateKey] || dateData.dayTotal
                }
                const finalDayTotal = (latestDayTotal && latestDayTotal !== '00:00:00')
                    ? latestDayTotal
                    : '-'

                sortedDates[dateKey] = {
                    ...dateData,
                    dayTotal: finalDayTotal,
                    entries: sortedEntries
                }
            })

            // Always use the LATEST backend weekly total from timeTotals
            // This ensures we show the updated value after loading more pages
            // The weekData.weekTotal might be stale if it was cached when the week was first created
            const latestWeekTotal = timeTotals.weekly_totals[weekKey] || weekData.weekTotal
            const finalWeekTotal = (latestWeekTotal && latestWeekTotal !== '00:00:00')
                ? latestWeekTotal
                : '-'

            sortedGroupedData[weekKey] = {
                ...weekData,
                weekTotal: finalWeekTotal,
                dates: sortedDates
            }
        })

        return sortedGroupedData
    }

    // Update current time every second (for display)
    useEffect(() => {
        const timer = setInterval(() => {
            const now = moment().toDate()
            setCurrentTime(getCurrentTimeFormatted())

            // Update elapsed time if timer is running
            if (isTimerRunning && timerStartTime) {
                const elapsed = Math.floor((now - timerStartTime) / 1000)
                const formattedElapsed = formatElapsedTime(elapsed)
                setElapsedTime(formattedElapsed)

                // Update tab title with timer
                const projectName = selectedProject || 'Timer'
                const taskDescription = description || 'Working'
                document.title = `⏱ ${formattedElapsed} - ${taskDescription} | Kadle`
            }
        }, 1000)

        return () => clearInterval(timer)
    }, [isTimerRunning, timerStartTime, selectedProject, description])

    // Reset tab title when timer stops
    useEffect(() => {
        if (!isTimerRunning) {
            document.title = 'Kadle - Time Tracking'
        }
    }, [isTimerRunning])

    const handleStartTimer = async () => {
        if (isTimerRunning) {
            // Check if project/task is selected before stopping
            if (!selectedTask && !selectedProject) {
                // Store timer data and show project selection modal
                const endTime = moment().toISOString()
                const stopData = {
                    user_id: userData.id,
                    end_time: endTime,
                    description: description || '',
                    billable: isBillable
                }
                setPendingTimerData(stopData)
                setIsProjectSelectionModalOpen(true)
                return
            }

            // Stop timer and call worklog stop API
            try {
                setTimerError(null) // Clear any previous errors

                // Clean hashtags from description
                const { cleanDescription } = parseMentions(description, userProjects, userTasks)

                const endTime = moment().toISOString()
                const stopData = {
                    user_id: userData.id,
                    end_time: endTime,
                    description: cleanDescription || '',
                    billable: isBillable
                }

                // Add project or task if selected
                if (selectedTask) {
                    stopData.task = selectedTask.id
                } else if (selectedProject) {
                    stopData.project = selectedProject.id
                }

                const result = await apiClient.stopWorklog(stopData)
                console.log('Worklog stopped:', result)

                // Check if timer was split across midnight
                if (result.split) {
                    setTimerSuccess('Timer crossed midnight - created 2 separate time entries')
                    // Auto-clear success message after 5 seconds
                    setTimeout(() => setTimerSuccess(null), 5000)
                }

                // Reset timer state only on successful API call
                setIsTimerRunning(false)
                setTimerStartTime(null)
                setElapsedTime('00:00:00')
                setDescription('')
                setSelectedProject(null)
                setSelectedTask(null)
                setIsBillable(false)

                // Refresh worklogs list to show the newly stopped worklog
                await refreshWorklogs()

            } catch (error) {
                console.error('Failed to stop worklog:', error)
                // Display error and keep timer running
                setTimerError(`Failed to stop worklog: ${error.message}`)
            }
        } else {
            // Start timer and create new running worklog
            try {
                setTimerError(null) // Clear any previous errors

                // Clean hashtags from description
                const { cleanDescription } = parseMentions(description, userProjects, userTasks)

                const startTime = moment().toISOString()
                const worklogData = {
                    user_id: userData.id,
                    start_time: startTime,
                    description: cleanDescription || '',
                    billable: isBillable
                }

                // Add project or task if selected
                if (selectedTask) {
                    worklogData.task = selectedTask.id
                } else if (selectedProject) {
                    worklogData.project = selectedProject.id
                }

                const result = await apiClient.createWorklog(worklogData)
                console.log('Running worklog created:', result)

                // Start timer only on successful API call
                setIsTimerRunning(true)
                setTimerStartTime(moment().toDate())
                setElapsedTime('00:00:00')

            } catch (error) {
                console.error('Failed to create worklog:', error)
                // Display error and don't start timer
                setTimerError(`Failed to start worklog: ${error.message}`)
            }
        }
    }

    const handleAddManualEntry = async () => {
        try {
            setTimerError(null) // Clear any previous errors

            // Note: Description is optional, allow empty descriptions

            if (!startTime || !endTime) {
                setTimerError('Start time and end time are required')
                return
            }

            // Validate that either a project or task is selected for completed worklogs
            if (!selectedProject && !selectedTask) {
                setTimerError('Please select a project or task for this worklog')
                return
            }

            // Parse and validate time inputs using selected date
            const startDateTime = moment(`${moment(selectedDate).format('YYYY-MM-DD')} ${startTime}`).toDate()
            const endDateTime = moment(`${moment(selectedDate).format('YYYY-MM-DD')} ${endTime}`).toDate()

            if (!moment(startDateTime).isValid() || !moment(endDateTime).isValid()) {
                setTimerError('Invalid time format. Please use HH:MM format')
                return
            }

            if (endDateTime <= startDateTime) {
                setTimerError('End time must be after start time')
                return
            }

            // Clean hashtags from description
            const { cleanDescription } = parseMentions(description, userProjects, userTasks)

            // Prepare worklog data
            const worklogData = {
                user_id: userData.id,
                start_time: startDateTime.toISOString(),
                end_time: endDateTime.toISOString(),
                description: cleanDescription || '',
                billable: isBillable
            }

            // Add project or task if selected
            if (selectedTask) {
                worklogData.task = selectedTask.id
            } else if (selectedProject) {
                worklogData.project = selectedProject.id
            }

            const result = await apiClient.createWorklog(worklogData)
            console.log('Manual worklog created:', result)

            // Clear form on success
            setDescription('')
            setStartTime('00:00')
            setEndTime('00:00')
            setSelectedProject(null)
            setSelectedTask(null)
            setIsBillable(false)

            // Refresh worklogs list to show the new entry
            await refreshWorklogs()

        } catch (error) {
            console.error('Failed to create manual worklog:', error)
            setTimerError(`Failed to create worklog: ${error.message}`)
        }
    }

    const handleContinueEntry = async (entry) => {
        // Continue/replay this entry by setting form values and auto-starting timer
        console.log('Continuing entry:', entry)

        // Set description from the entry
        setDescription(entry.description === '<no description>' ? '' : entry.description)

        // Set billable status from the original entry
        setIsBillable(entry.originalWorklog?.billable || false)

        // Find and set project/task from the original worklog data
        const originalWorklog = entry.originalWorklog
        if (originalWorklog) {
            // Clear previous selections
            setSelectedProject(null)
            setSelectedTask(null)

            // Set project if available
            if (originalWorklog.project_id && userProjects.length > 0) {
                const matchedProject = userProjects.find(p => p.id === originalWorklog.project_id)
                if (matchedProject) {
                    setSelectedProject(matchedProject)
                }
            }

            // Set task if available
            if (originalWorklog.task_id && userTasks.length > 0) {
                const matchedTask = userTasks.find(t => t.id === originalWorklog.task_id)
                if (matchedTask) {
                    setSelectedTask(matchedTask)
                    // Also set the project for the task
                    const taskProject = userProjects.find(p => p.id === matchedTask.project_id)
                    if (taskProject) {
                        setSelectedProject(taskProject)
                    }
                }
            }
        }

        // Close dropdown first
        setOpenDropdownId(null)

        // Auto-start timer if not already running and user data is available
        if (!isTimerRunning && userData) {
            try {
                setTimerError(null)
                const startTime = moment().toISOString()
                const worklogData = {
                    user_id: userData.id,
                    start_time: startTime,
                    description: entry.description === '<no description>' ? '' : entry.description,
                    billable: originalWorklog?.billable || false
                }

                // Add project or task if available from original worklog
                if (originalWorklog?.task_id) {
                    worklogData.task = originalWorklog.task_id
                } else if (originalWorklog?.project_id) {
                    worklogData.project = originalWorklog.project_id
                }

                const result = await apiClient.createWorklog(worklogData)
                console.log('Auto-started worklog from continue:', result)

                // Start timer only on successful API call
                setIsTimerRunning(true)
                setTimerStartTime(moment().toDate())
                setElapsedTime('00:00:00')

            } catch (error) {
                console.error('Failed to auto-start worklog from continue:', error)
                setTimerError(`Failed to start worklog: ${error.message}`)
            }
        }
    }

    const handleDuplicateEntry = async (entry) => {
        if (!entry.originalWorklog) {
            console.error('❌ Original worklog data not available for duplication')
            setTimerError('Cannot duplicate entry: missing original data')
            setOpenDropdownId(null)
            return
        }

        if (!userData) {
            console.error('❌ User data not available for duplication')
            setTimerError('Cannot duplicate: user data not loaded')
            setOpenDropdownId(null)
            return
        }

        try {
            setTimerError(null) // Clear any previous errors

            const originalWorklog = entry.originalWorklog

            // Create worklog data using the same structure as the original
            const duplicateWorklogData = {
                user_id: userData.id,
                start_time: originalWorklog.start_time,
                end_time: originalWorklog.end_time,
                description: originalWorklog.description || 'No description',
                logged_date: originalWorklog.logged_date, // Preserve the exact same logged_date for record-keeping
                billable: originalWorklog.billable || false // Preserve billable status
            }

            // Add project or task based on the original worklog's workable type
            // Use workable_id directly when available, fall back to name matching if needed
            if (originalWorklog.workable_type === 'Task') {
                if (originalWorklog.task_id || originalWorklog.workable_id) {
                    // Use the actual task ID if available
                    duplicateWorklogData.task = originalWorklog.task_id || originalWorklog.workable_id
                } else if (originalWorklog.workable_name) {
                    // Fall back to name matching for older data
                    const matchedTask = availableTasks.find(task => task.name === originalWorklog.workable_name)
                    if (matchedTask) {
                        duplicateWorklogData.task = matchedTask.id
                    } else {
                        console.error('Task not found in available tasks:', originalWorklog.workable_name)
                        setTimerError(`Cannot duplicate: task "${originalWorklog.workable_name}" not found in available tasks`)
                        setOpenDropdownId(null)
                        return
                    }
                }
            } else if (originalWorklog.workable_type === 'Project') {
                if (originalWorklog.project_id) {
                    // Use the actual project ID if available
                    duplicateWorklogData.project = originalWorklog.project_id
                } else if (originalWorklog.project_name) {
                    // Fall back to name matching for older data
                    const matchedProject = availableProjects.find(project => project.name === originalWorklog.project_name)
                    if (matchedProject) {
                        duplicateWorklogData.project = matchedProject.id
                    } else {
                        console.error('Project not found in available projects:', originalWorklog.project_name)
                        setTimerError(`Cannot duplicate: project "${originalWorklog.project_name}" not found in available projects`)
                        setOpenDropdownId(null)
                        return
                    }
                }
            } else {
                // Fallback: If no workable is found, we cannot create a completed worklog
                console.error('Original worklog missing workable association:', originalWorklog)
                setTimerError('Cannot duplicate: original entry has no project or task association')
                setOpenDropdownId(null)
                return
            }

            // Validate that we have either project or task before making API call
            if (!duplicateWorklogData.task && !duplicateWorklogData.project) {
                console.error('Neither task nor project specified for worklog duplication')
                setTimerError('Cannot duplicate: missing project or task association')
                setOpenDropdownId(null)
                return
            }

            console.log('Duplicating worklog with data:', duplicateWorklogData)

            // Create the duplicate worklog
            const result = await apiClient.createWorklog(duplicateWorklogData)
            console.log('Worklog duplicated successfully:', result)

            // Close dropdown
            setOpenDropdownId(null)

            // Refresh worklogs to show the new duplicated entry
            await refreshWorklogs()

        } catch (error) {
            console.error('Failed to duplicate worklog:', error)
            // Parse API error response for better user feedback
            let errorMessage = error.message
            if (error.message.includes('workable')) {
                errorMessage = 'Cannot duplicate: missing project or task association'
            }
            setTimerError(`Failed to duplicate worklog: ${errorMessage}`)
            setOpenDropdownId(null)
        }
    }

    const handleDeleteEntry = async (entryId) => {
        // Delete this entry
        console.log('Deleting entry:', entryId)
        try {
            await apiClient.deleteWorklog(entryId)
            // Refresh worklogs after deletion
            await refreshWorklogs()
        } catch (error) {
            console.error('Failed to delete worklog:', error)
            setTimerError(`Failed to delete worklog: ${error.message}`)
        }
        setOpenDropdownId(null)
    }

    const handleSplitEntry = (entry) => {
        console.log('Splitting entry:', entry)
        setSplitEntry(entry)

        // Calculate split time as middle of the duration
        const originalWorklog = entry.originalWorklog
        if (originalWorklog) {
            const startTime = moment(originalWorklog.start_time)
            const endTime = moment(originalWorklog.end_time)
            const duration = endTime - startTime
            const middleTime = Math.floor(duration / 2)
            setSplitTime(middleTime)
        }

        setShowSplitModal(true)
        setOpenDropdownId(null)
    }

    const handleSplitConfirm = async () => {
        if (!splitEntry || !splitEntry.originalWorklog) return

        try {
            setTimerError(null)
            const originalWorklog = splitEntry.originalWorklog

            // Calculate new times
            const startTime = moment(originalWorklog.start_time)
            const endTime = moment(originalWorklog.end_time)
            const splitDateTime = moment(startTime).add(splitTime, 'milliseconds').toDate()

            // Prepare data for the first part (original entry update)
            const firstPartData = {
                end_time: splitDateTime.toISOString()
            }

            // Prepare data for the second part (new entry)
            const secondPartData = {
                user_id: userData.id,
                start_time: splitDateTime.toISOString(),
                end_time: endTime.toISOString(),
                description: originalWorklog.description || '',
                logged_date: originalWorklog.logged_date, // Preserve original logged_date for record-keeping
                billable: originalWorklog.billable || false
            }

            // Add project or task based on original worklog
            if (originalWorklog.task_id) {
                secondPartData.task = originalWorklog.task_id
            } else if (originalWorklog.project_id) {
                secondPartData.project = originalWorklog.project_id
            }

            // Update the original worklog (first part)
            await apiClient.updateWorklog(originalWorklog.id, firstPartData)

            // Create the new worklog (second part)
            await apiClient.createWorklog(secondPartData)

            console.log('Worklog split successfully')

            // Close modal and refresh worklogs
            setShowSplitModal(false)
            setSplitEntry(null)
            await refreshWorklogs()

        } catch (error) {
            console.error('Failed to split worklog:', error)
            setTimerError(`Failed to split worklog: ${error.message}`)
        }
    }

    const handleSplitCancel = () => {
        setShowSplitModal(false)
        setSplitEntry(null)
    }

    const handleMoreButtonClick = (entryId, event) => {
        console.log('🔘 MORE BUTTON CLICKED for entry:', entryId)
        console.log('📋 Current openDropdownId:', openDropdownId)
        event.stopPropagation()
        const newOpenId = openDropdownId === entryId ? null : entryId
        console.log('📋 Setting openDropdownId to:', newOpenId)
        if (newOpenId) {
            const buttonRect = event.currentTarget.getBoundingClientRect()
            setDropdownPosition({
                top: buttonRect.bottom + 4,
                right: window.innerWidth - buttonRect.right,
            })
        } else {
            setDropdownPosition(null)
        }
        setOpenDropdownId(newOpenId)
    }

    // Function to update worklog field
    const updateWorklogField = async (worklogId, field, value) => {
        try {
            const updateData = { [field]: value }
            const response = await apiClient.updateWorklog(worklogId, updateData)

            // Update the worklog in-place instead of refreshing all worklogs
            // This preserves the pagination state and prevents infinite scroll from breaking
            if (response && response.worklog) {
                setRecentWorklogs(prev =>
                    prev.map(w => w.id === worklogId ? { ...w, ...response.worklog } : w)
                )
            } else {
                // Fallback: update just the field that changed
                setRecentWorklogs(prev =>
                    prev.map(w => w.id === worklogId ? { ...w, [field]: value } : w)
                )
            }
        } catch (error) {
            console.error('Failed to update worklog:', error)
            setTimerError(`Failed to update ${field}: ${error.message}`)
        }
    }

    // Function to handle billable toggle
    const handleBillableToggle = async (worklogId, currentBillable) => {
        await updateWorklogField(worklogId, 'billable', !currentBillable)
    }

    // Function to handle description change
    const handleDescriptionChange = async (worklogId, newDescription) => {
        await updateWorklogField(worklogId, 'description', newDescription)
    }

    // Function to handle running timer description update
    const handleRunningDescriptionChange = async (newDescription) => {
        if (!isTimerRunning || !userData) return

        // Find the running worklog
        const runningWorklog = recentWorklogs.find(worklog => worklog.status === 'running')
        if (runningWorklog) {
            try {
                await apiClient.updateWorklog(runningWorklog.id, { description: newDescription })
                // Update local state
                setDescription(newDescription)
            } catch (error) {
                console.error('Failed to update running timer description:', error)
                setTimerError(`Failed to update description: ${error.message}`)
            }
        }
    }

    // Function to handle time change
    const handleTimeChange = async (worklogId, field, newTime) => {
        // Normalize the time input first
        const normalizedTime = normalizeTimeInput(newTime)

        // Find the original worklog to get its date
        const originalWorklog = recentWorklogs.find(w => w.id === worklogId)
        if (!originalWorklog) {
            console.error('Original worklog not found for time change')
            return
        }

        // Use the original worklog's start_time date for time inputs (this is what matters for grouping)
        const originalDate = moment(originalWorklog.start_time)
        const dateString = originalDate.format('YYYY-MM-DD')
        const datetime = moment(`${dateString} ${normalizedTime}`).toDate()

        if (moment(datetime).isValid()) {
            await updateWorklogField(worklogId, field, datetime.toISOString())
        }
    }

    // Function to handle date change for worklogs
    const handleWorklogDateChange = async (worklogId, newDate) => {
        console.log('🚀 handleWorklogDateChange called with:', { worklogId, newDate })

        try {
            // Validate inputs
            if (!worklogId) {
                console.error('❌ No worklog ID provided')
                setTimerError('Cannot update date: no worklog ID provided')
                return
            }

            if (!newDate || !moment(newDate).isValid()) {
                console.error('❌ Invalid date provided:', newDate)
                setTimerError('Cannot update date: invalid date provided')
                return
            }

            // Find the original worklog to get current times
            const originalWorklog = recentWorklogs.find(w => w.id === worklogId)
            console.log('🔍 Found original worklog:', originalWorklog)

            if (!originalWorklog) {
                console.error('❌ Original worklog not found for ID:', worklogId)
                setTimerError('Cannot update date: worklog not found')
                return
            }

            // Extract current times and apply to new date
            const currentStart = moment(originalWorklog.start_time).toDate()
            const currentEnd = moment(originalWorklog.end_time).toDate()

            console.log('⏰ Current times - Start:', currentStart, 'End:', currentEnd)

            // Create new datetime objects with new date but same times
            const newStartDate = moment(newDate).toDate()
            newStartDate.setHours(currentStart.getHours(), currentStart.getMinutes(), currentStart.getSeconds())

            const newEndDate = moment(newDate).toDate()
            newEndDate.setHours(currentEnd.getHours(), currentEnd.getMinutes(), currentEnd.getSeconds())

            console.log('⏰ New times - Start:', newStartDate, 'End:', newEndDate)

            // Update start_time, end_time, and logged_date with the new date
            const updateData = {
                start_time: newStartDate.toISOString(),
                end_time: newEndDate.toISOString(),
                logged_date: formatDateForBackend(newDate) // Use local timezone date
            }

            // Use centralized date formatting function

            console.log('📤 Sending update data:', updateData)
            console.log('🔄 Updating worklog date:', worklogId, 'to', moment(newDate).format('YYYY-MM-DD'))

            await apiClient.updateWorklog(worklogId, updateData)
            console.log('✅ Worklog update successful!')

            // Close the date picker first
            setEntryDatePickers(prev => ({ ...prev, [worklogId]: false }))

            console.log('🔄 Date update successful, refreshing page...')

            // Store the worklog ID in sessionStorage so we can scroll to it after page reload
            sessionStorage.setItem('scrollToWorklogId', worklogId)

            // Force a complete page refresh to ensure we get the updated data
            window.location.reload()

        } catch (error) {
            console.error('Failed to update worklog date:', error)
            setTimerError(`Failed to update date: ${error.message}`)
        }
    }

    // Function to scroll to a specific worklog entry
    const scrollToWorklog = (worklogId) => {
        try {
            // Find the DOM element for this worklog
            const worklogElement = document.querySelector(`[data-worklog-id="${worklogId}"]`)

            if (worklogElement) {
                // Just scroll to the element without any highlight effect
                worklogElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }
        } catch (error) {
            console.error('Error scrolling to worklog:', error)
        }
    }

    // Function to toggle date picker for specific entry
    const toggleEntryDatePicker = (worklogId) => {
        setEntryDatePickers(prev => ({
            ...prev,
            [worklogId]: !prev[worklogId]
        }))
    }

    // Function to handle project/task change for existing worklogs
    const handleWorklogProjectChange = async (worklogId, project, task) => {
        try {
            const updateData = {}

            if (task) {
                updateData.task = task.id
                updateData.project = null // Clear project when task is set
            } else if (project) {
                updateData.project = project.id
                updateData.task = null // Clear task when project is set
            } else {
                updateData.project = null
                updateData.task = null
            }

            const response = await apiClient.updateWorklog(worklogId, updateData)

            // Update the worklog in-place instead of refreshing all worklogs
            // This preserves the pagination state and prevents infinite scroll from breaking
            if (response && response.worklog) {
                setRecentWorklogs(prev =>
                    prev.map(w => w.id === worklogId ? { ...w, ...response.worklog } : w)
                )
            } else {
                // Fallback: update with the data we sent
                setRecentWorklogs(prev =>
                    prev.map(w => w.id === worklogId ? { ...w, ...updateData } : w)
                )
            }
        } catch (error) {
            console.error('Failed to update worklog project/task:', error)
            setTimerError(`Failed to update project/task: ${error.message}`)
        }
    }

    // Helper function to extract time from datetime string
    const formatTimeForInput = (datetimeString) => {
        if (!datetimeString) return ''
        const date = new Date(datetimeString)
        // Use formatInTimeZone to convert UTC time to user's timezone
        return formatTime24Hour(date, userTimezone)
    }

    // Helper function to normalize time input (handles "2200" -> "22:00", "930" -> "09:30", and "22:00")
    const normalizeTimeInput = (input) => {
        if (!input) return ''

        // Remove spaces and non-digit characters except colon
        const cleaned = input.replace(/[^\d:]/g, '')

        // Handle different formats
        if (/^\d{3}$/.test(cleaned)) {
            // Format: "930" -> "09:30" (HMM format)
            const hours = cleaned.slice(0, 1)
            const minutes = cleaned.slice(1, 3)
            return `${hours.padStart(2, '0')}:${minutes}`
        } else if (/^\d{4}$/.test(cleaned)) {
            // Format: "2200" -> "22:00" (HHMM format)
            return `${cleaned.slice(0, 2)}:${cleaned.slice(2, 4)}`
        } else if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
            // Format: "22:00" or "9:00" -> ensure 2-digit hours
            const [hour, minute] = cleaned.split(':')
            return `${hour.padStart(2, '0')}:${minute}`
        } else if (/^\d{1,2}$/.test(cleaned)) {
            // Format: "22" -> "22:00"
            return `${cleaned.padStart(2, '0')}:00`
        }

        // Return original if no pattern matches
        return input
    }

    // Helper function to get current project and task for a worklog entry
    const getWorklogProjectAndTask = (entry) => {
        const originalWorklog = entry.originalWorklog
        if (!originalWorklog) return { currentProject: null, currentTask: null }

        let currentProject = null
        let currentTask = null

        // Find current project
        if (originalWorklog.project_id && userProjects.length > 0) {
            currentProject = userProjects.find(p => p.id === originalWorklog.project_id)
        }

        // Find current task
        if (originalWorklog.task_id && userTasks.length > 0) {
            currentTask = userTasks.find(t => t.id === originalWorklog.task_id)
        }

        return { currentProject, currentTask }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Handle entry dropdown
            if (!event.target.closest('.entryDropdown') && !event.target.closest('.moreButton')) {
                setOpenDropdownId(null)
                setDropdownPosition(null)
            }

            // Handle main date picker - only close if not clicking on date section or date picker
            const dateSection = event.target.closest('[data-date-section]')
            const datePicker = event.target.closest('[data-date-picker]')

            if (!dateSection && !datePicker) {
                setIsDatePickerOpen(false)
            }

            // Handle entry date pickers - close if not clicking on entry date section or date picker
            const entryDateSection = event.target.closest('[data-entry-date-section]')
            const entryDatePicker = event.target.closest('[data-entry-date-picker]')

            if (!entryDateSection && !entryDatePicker) {
                setEntryDatePickers({})
            }
        }

        // Close portal dropdown on scroll so it doesn't float detached from its button
        const handleScroll = () => {
            if (openDropdownId) {
                setOpenDropdownId(null)
                setDropdownPosition(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('scroll', handleScroll, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('scroll', handleScroll, true)
        }
    }, [openDropdownId])

    const handleProjectSelect = (project) => {
        setSelectedProject(project)

        // Auto-set billable based on project if no task is selected
        if (project && project.is_billable !== undefined && !selectedTask) {
            setIsBillable(project.is_billable)
        }
    }

    const handleTaskSelect = (task) => {
        setSelectedTask(task)

        // Auto-set billable based on task selection
        if (task && task.is_billable !== undefined) {
            setIsBillable(task.is_billable)
        } else if (!task && selectedProject && selectedProject.is_billable !== undefined) {
            // Task cleared, fall back to project's billable status
            setIsBillable(selectedProject.is_billable)
        }
    }

    const handleClearProjectTask = () => {
        setSelectedProject(null)
        setSelectedTask(null)
    }

    // Pagination handlers for projects and tasks only
    const handleProjectsPageChange = (page) => {
        const newParams = { ...paginationParams, projects_page: page }
        setPaginationParams(newParams)
        loadUserData(newParams)
    }

    const handleProjectsPerPageChange = (perPage, page = 1) => {
        const newParams = { ...paginationParams, projects_per_page: perPage, projects_page: page }
        setPaginationParams(newParams)
        loadUserData(newParams)
    }

    const handleTasksPageChange = (page) => {
        const newParams = { ...paginationParams, tasks_page: page }
        setPaginationParams(newParams)
        loadUserData(newParams)
    }

    const handleTasksPerPageChange = (perPage, page = 1) => {
        const newParams = { ...paginationParams, tasks_per_page: perPage, tasks_page: page }
        setPaginationParams(newParams)
        loadUserData(newParams)
    }

    // Parse description for auto-population of project and task
    const parseDescription = (description) => {
        // Format: <description>@<project>@<task>
        // Example: "Standup@Modi@Dev"
        const match = description.match(/^(.+?)@(.+?)(?:@(.+))?$/)

        if (!match) return null

        const [, desc, projectPattern, taskPattern] = match

        return {
            description: desc.trim(),
            projectPattern: projectPattern?.trim(),
            taskPattern: taskPattern?.trim()
        }
    }

    // Find best matching project based on pattern
    const findMatchingProject = (pattern, projectsList) => {
        if (!pattern) return null

        const lowerPattern = pattern.toLowerCase()

        // Try exact match first (case-insensitive)
        const exactMatch = projectsList.find(p =>
            p.name.toLowerCase() === lowerPattern
        )
        if (exactMatch) return exactMatch

        // Try starts-with match
        const startsWithMatch = projectsList.find(p =>
            p.name.toLowerCase().startsWith(lowerPattern)
        )
        if (startsWithMatch) return startsWithMatch

        // Try contains match
        const containsMatch = projectsList.find(p =>
            p.name.toLowerCase().includes(lowerPattern)
        )

        return containsMatch
    }

    // Find best matching task based on pattern
    const findMatchingTask = (pattern, tasksList) => {
        if (!pattern || !tasksList || tasksList.length === 0) return null

        const lowerPattern = pattern.toLowerCase()

        // Try exact match first (case-insensitive)
        const exactMatch = tasksList.find(t =>
            t.name.toLowerCase() === lowerPattern
        )
        if (exactMatch) return exactMatch

        // Try starts-with match
        const startsWithMatch = tasksList.find(t =>
            t.name.toLowerCase().startsWith(lowerPattern)
        )
        if (startsWithMatch) return startsWithMatch

        // Try contains match
        const containsMatch = tasksList.find(t =>
            t.name.toLowerCase().includes(lowerPattern)
        )

        return containsMatch
    }

    // Capitalize first letter of a sentence
    const capitalizeFirstLetter = (text) => {
        if (!text) return text
        return text.charAt(0).toUpperCase() + text.slice(1)
    }

    // Handle Tab key press for auto-population
    const handleDescriptionKeyDown = (e) => {
        if (e.key === 'Tab' && description) {
            e.preventDefault() // Prevent default tab behavior

            const parsed = parseDescription(description)

            if (parsed && parsed.projectPattern) {
                const matchedProject = findMatchingProject(parsed.projectPattern, userProjects)

                if (matchedProject) {
                    // Set the description without the pattern (capitalized)
                    const capitalizedDescription = capitalizeFirstLetter(parsed.description)
                    setDescription(capitalizedDescription)

                    // Set the matched project
                    setSelectedProject(matchedProject)

                    // Set billable based on project
                    if (matchedProject.is_billable !== undefined) {
                        setIsBillable(matchedProject.is_billable)
                    }

                    // Try to match task if pattern exists
                    if (parsed.taskPattern && userTasks.length > 0) {
                        // Filter tasks to only those belonging to the matched project
                        const projectTasks = userTasks.filter(t => t.project_id === matchedProject.id)
                        const matchedTask = findMatchingTask(parsed.taskPattern, projectTasks)

                        if (matchedTask) {
                            setSelectedTask(matchedTask)
                            // Override billable with task's billable status if task is matched
                            if (matchedTask.is_billable !== undefined) {
                                setIsBillable(matchedTask.is_billable)
                            }
                        } else {
                            setSelectedTask(null)
                        }
                    } else {
                        setSelectedTask(null)
                    }

                    // Pattern with @ found - move focus to start button
                    setTimeout(() => {
                        startButtonRef.current?.focus()
                    }, 100)
                } else {
                    // No project match found, just capitalize the description
                    const capitalizedDescription = capitalizeFirstLetter(parsed.description)
                    setDescription(capitalizedDescription)

                    // Pattern with @ found but no match - move focus to start button
                    setTimeout(() => {
                        startButtonRef.current?.focus()
                    }, 100)
                }
            } else {
                // No pattern found, just capitalize the entire description
                const capitalizedDescription = capitalizeFirstLetter(description)
                setDescription(capitalizedDescription)

                // No @ pattern - move focus to project button for manual selection
                setTimeout(() => {
                    projectButtonRef.current?.focus()
                }, 100)
            }
        }
    }

    const handleDescriptionSuggestionSelect = async (suggestion) => {
        // Auto-populate project and billable status based on the selected suggestion
        setIsBillable(suggestion.billable || false)

        // Find and set the project
        if (suggestion.project_id && userProjects.length > 0) {
            const matchedProject = userProjects.find(p => p.id === suggestion.project_id)
            if (matchedProject) {
                setSelectedProject(matchedProject)
            }
        }

        // Find and set the task if present
        if (suggestion.task_id && userTasks.length > 0) {
            const matchedTask = userTasks.find(t => t.id === suggestion.task_id)
            if (matchedTask) {
                setSelectedTask(matchedTask)
            }
        } else {
            setSelectedTask(null)
        }

        // Auto-start timer if not already running and not in manual entry mode
        if (!isTimerRunning && !isManualEntry && userData) {
            try {
                setTimerError(null)
                const startTime = moment().toISOString()
                const worklogData = {
                    user_id: userData.id,
                    start_time: startTime,
                    description: suggestion.description,
                    billable: suggestion.billable || false
                }

                // Add project or task if available from suggestion
                if (suggestion.task_id) {
                    worklogData.task = suggestion.task_id
                } else if (suggestion.project_id) {
                    worklogData.project = suggestion.project_id
                }

                const result = await apiClient.createWorklog(worklogData)
                console.log('Auto-started worklog from suggestion:', result)

                // Start timer only on successful API call
                setIsTimerRunning(true)
                setTimerStartTime(moment().toDate())
                setElapsedTime('00:00:00')

            } catch (error) {
                console.error('Failed to auto-start worklog from suggestion:', error)
                setTimerError(`Failed to start worklog: ${error.message}`)
            }
        }
    }

    const handleDiscardTimer = () => {
        setShowDiscardConfirmation(true)
    }

    const confirmDiscardTimer = async () => {
        if (!isTimerRunning || !userData) return

        try {
            setTimerError(null)

            // Delete the running worklog
            await apiClient.deleteRunningWorklog(userData.id)
            console.log('Running worklog discarded successfully')

            // Reset timer state
            setIsTimerRunning(false)
            setTimerStartTime(null)
            setElapsedTime('00:00:00')
            setDescription('')
            setSelectedProject(null)
            setSelectedTask(null)
            setIsBillable(false)

            // Refresh worklogs list to remove the discarded entry
            await refreshWorklogs()

        } catch (error) {
            console.error('Failed to discard running worklog:', error)
            setTimerError(`Failed to discard timer: ${error.message}`)
        } finally {
            setShowDiscardConfirmation(false)
        }
    }

    const cancelDiscardTimer = () => {
        setShowDiscardConfirmation(false)
    }

    // Modal handlers for project selection
    const handleProjectTaskSelection = async (selection) => {
        if (!pendingTimerData) return

        try {
            setTimerError(null)
            const stopData = { ...pendingTimerData }

            // Add the selected project or task
            if (selection.task) {
                stopData.task = selection.task.id
                setSelectedTask(selection.task)
                setSelectedProject(selection.project)
            } else if (selection.project) {
                stopData.project = selection.project.id
                setSelectedProject(selection.project)
            }

            // Update billable status if provided
            if (selection.hasOwnProperty('billable')) {
                stopData.billable = selection.billable
                setIsBillable(selection.billable)
            }

            const result = await apiClient.stopWorklog(stopData)
            console.log('Worklog stopped with project selection:', result)

            // Check if timer was split across midnight
            if (result.split) {
                setTimerSuccess('Timer crossed midnight - created 2 separate time entries')
                // Auto-clear success message after 5 seconds
                setTimeout(() => setTimerSuccess(null), 5000)
            }

            // Reset timer state only on successful API call
            setIsTimerRunning(false)
            setTimerStartTime(null)
            setElapsedTime('00:00:00')
            setDescription('')
            setSelectedProject(null)
            setSelectedTask(null)
            setIsBillable(false)

            // Clear pending data
            setPendingTimerData(null)

            // Refresh worklogs list to show the newly stopped worklog
            await refreshWorklogs()

        } catch (error) {
            console.error('Failed to stop worklog with project selection:', error)
            setTimerError(`Failed to stop worklog: ${error.message}`)
        }
    }

    const handleCloseProjectModal = () => {
        setIsProjectSelectionModalOpen(false)
        setPendingTimerData(null)
    }

    const getProjectButtonText = () => {
        if (selectedProject && selectedTask) {
            return (
                <span>
                    <span className={styles.projectName}>{selectedProject.name}</span>
                    <span className={styles.taskSeparator}> &gt; </span>
                    <span className={styles.taskName}>{selectedTask.name}</span>
                </span>
            )
        } else if (selectedProject) {
            return <span className={styles.projectName}>{selectedProject.name}</span>
        }
        return 'Project'
    }

    // Show loading state
    if (loading) {
        return (
            <div className={styles.timelogView}>
                <div className={styles.loadingState}>
                    <p>Loading your time tracking data...</p>
                </div>
            </div>
        )
    }

    // Show error state
    if (error) {
        return (
            <div className={styles.timelogView}>
                <div className={styles.errorState}>
                    <p>Error: {error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        )
    }

    // Show message if no API token provided
    if (!apiToken) {
        return (
            <div className={styles.timelogView}>
                <div className={styles.errorState}>
                    <p>API token not available. Please refresh the page.</p>
                    <button onClick={() => window.location.reload()}>Refresh</button>
                </div>
            </div>
        )
    }

    const worklogData = processWorklogsData(recentWorklogs)

    return (
        <div className={styles.timelogView}>
            {/* Top Control Bar */}
            <div className={styles.topBar}>
                <div className={styles.descriptionSection}>
                    <DescriptionAutofill
                        value={description}
                        onChange={(newDescription) => {
                            setDescription(newDescription)
                            // If timer is running, save description immediately
                            if (isTimerRunning) {
                                // Debounce the API call to avoid too many requests
                                if (descriptionTimeoutRef.current) {
                                    clearTimeout(descriptionTimeoutRef.current)
                                }
                                descriptionTimeoutRef.current = setTimeout(() => {
                                    handleRunningDescriptionChange(newDescription)
                                }, 500)
                            }
                        }}
                        onSuggestionSelect={handleDescriptionSuggestionSelect}
                        onKeyDown={handleDescriptionKeyDown}
                        placeholder={isManualEntry ? "What have you worked on? Tip: Use #project #task @<user> format" : "What are you working on? Tip: Use #project #task @<user> format"}
                        className={styles.descriptionInput}
                        apiToken={apiToken}
                        userId={userData?.id}
                        projects={userProjects}
                        tasks={userTasks}
                        teammates={teammates}
                        selectedProject={selectedProject}
                        onProjectTaskMention={({ type, item }) => {
                            if (type === 'project') {
                                setSelectedProject(item)
                                if (item.is_billable !== undefined) {
                                    setIsBillable(item.is_billable)
                                }
                            } else if (type === 'task') {
                                setSelectedTask(item)
                                if (item.is_billable !== undefined) {
                                    setIsBillable(item.is_billable)
                                }
                            }
                        }}
                    />
                </div>

                <div className={styles.controlsSection}>
                    <ProjectDropdown
                        userProjects={userProjects}
                        userTasks={userTasks}
                        selectedProject={selectedProject}
                        selectedTask={selectedTask}
                        onProjectSelect={handleProjectSelect}
                        onTaskSelect={handleTaskSelect}
                        onClear={handleClearProjectTask}
                        trigger={
                            <button ref={projectButtonRef} className={styles.projectButton}>
                                <span className={styles.projectIcon}>⊕</span>
                                <span>{getProjectButtonText()}</span>
                            </button>
                        }
                    />

                    {isManualEntry ? (
                        <>
                            <div className={styles.timeInputs}>
                                <input
                                    type="text"
                                    className={styles.timeInput}
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={(e) => {
                                        const normalizedTime = normalizeTimeInput(e.target.value)
                                        setStartTime(normalizedTime)
                                    }}
                                />
                                <span className={styles.timeSeparator}>-</span>
                                <input
                                    type="text"
                                    className={styles.timeInput}
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={(e) => {
                                        const normalizedTime = normalizeTimeInput(e.target.value)
                                        setEndTime(normalizedTime)
                                    }}
                                />
                            </div>

                            <div className={styles.manualDateSection} data-date-section>
                                <button
                                    className={styles.dateButton}
                                    onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                >
                                    <span className={styles.calendarIcon}>📅</span>
                                    <span>
                                        {isToday(selectedDate)
                                            ? 'Today'
                                            : formatDisplayDate(selectedDate)
                                        }
                                    </span>
                                </button>
                                {isDatePickerOpen && (
                                    <div className={styles.datePickerWrapper} data-date-picker>
                                        <DatePickerComponent
                                            selectedDate={selectedDate}
                                            onDateSelect={(date) => {
                                                setSelectedDate(date)
                                                setIsDatePickerOpen(false)
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <button
                                className={styles.addButton}
                                onClick={handleAddManualEntry}
                            >
                                ADD
                            </button>
                        </>
                    ) : (
                        <>
                            <div className={styles.duration}>
                                {isTimerRunning ? elapsedTime : '00:00:00'}
                            </div>

                            <button
                                ref={startButtonRef}
                                className={styles.startButton}
                                onClick={handleStartTimer}
                                style={{
                                    background: isTimerRunning ? '#dc2626' : 'var(--color-primary)',
                                    color: 'var(--color-text-inverse)'
                                }}
                            >
                                {isTimerRunning ? 'STOP' : 'START'}
                            </button>
                        </>
                    )}

                    {isTimerRunning ? (
                        <button
                            className={styles.discardButton}
                            onClick={handleDiscardTimer}
                            title="Discard current timer"
                        >
                            <span className={styles.discardIcon}>🗑</span>
                        </button>
                    ) : (
                        <button
                            className={styles.modeToggle}
                            onClick={() => setIsManualEntry(!isManualEntry)}
                            title="Toggle manual entry mode"
                        >
                            <span className={styles.clockIcon}>🕘</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Timer Error Display */}
            {timerError && (
                <div className={styles.errorMessage}>
                    <span>{timerError}</span>
                    <button
                        className={styles.closeErrorButton}
                        onClick={() => setTimerError(null)}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Timer Success Display */}
            {timerSuccess && (
                <div className={styles.successMessage}>
                    <span>{timerSuccess}</span>
                    <button
                        className={styles.closeSuccessButton}
                        onClick={() => setTimerSuccess(null)}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Discard Confirmation Dialog */}
            {showDiscardConfirmation && (
                <div className={styles.confirmationOverlay}>
                    <div className={styles.confirmationDialog}>
                        <h3>Discard Timer?</h3>
                        <p>Are you sure you want to discard the current timer? This will delete the running worklog and
                            cannot be undone.</p>
                        <div className={styles.confirmationButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={cancelDiscardTimer}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={confirmDiscardTimer}
                            >
                                Discard Timer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Split Modal Dialog */}
            {showSplitModal && splitEntry && (
                <div className={styles.confirmationOverlay}>
                    <div className={styles.splitModal}>
                        <div className={styles.splitModalHeader}>
                            <h3>Split</h3>
                            <button
                                className={styles.closeButton}
                                onClick={handleSplitCancel}
                            >
                                ✕
                            </button>
                        </div>

                        <div className={styles.splitModalContent}>
                            <div className={styles.splitTimeLabel}>
                                Split at:
                            </div>

                            <div className={styles.splitTimeDisplay}>
                                {splitModalData.splitTimeFormatted}
                            </div>

                            <div className={styles.splitSliderContainer}>
                                <input
                                    type="range"
                                    className={styles.splitSlider}
                                    min={0}
                                    max={splitModalData.maxDuration}
                                    value={splitTime}
                                    onChange={(e) => setSplitTime(parseInt(e.target.value))}
                                />

                                <div className={styles.splitTimeLabels}>
                                    <span>{splitModalData.startTimeFormatted}</span>
                                    <span>{splitModalData.endTimeFormatted}</span>
                                </div>

                                <div className={styles.splitDurationLabels}>
                                    <span>{splitModalData.firstDuration}</span>
                                    <span>{splitModalData.secondDuration}</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.splitModalActions}>
                            <button
                                className={styles.cancelButton}
                                onClick={handleSplitCancel}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.saveButton}
                                onClick={handleSplitConfirm}
                            >
                                SAVE
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Worklogs List */}
            <div className={styles.worklogsList}>
                {Object.entries(worklogData).map(([weekRange, weekData]) => (
                    <div key={weekRange} className={styles.weekSection}>
                        {/* Week Header */}
                        <div className={styles.weekHeader}>
                            <span className={styles.weekRange}>{weekRange}</span>
                            <span className={styles.weekTotal}>Week total: {weekData.weekTotal}</span>
                        </div>

                        {/* Date Sections */}
                        {Object.entries(weekData.dates).map(([dateLabel, dateData]) => (
                            <div key={dateLabel} className={styles.dateSection}>
                                {/* Date Header */}
                                <div className={styles.dateHeader}>
                                    <span className={styles.dateLabel}>{dateData.displayDate || formatWorklogDate(dateLabel)}</span>
                                    <div className={styles.dateActions}>
                                        <span className={styles.dayTotal}>Total: {dateData.dayTotal}</span>
                                        <button className={styles.duplicateButton}>
                                            <span className={styles.duplicateIcon}>📋</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Entries for this date */}
                                <div className={styles.entriesList}>
                                    {dateData.entries.map((entry, entryIndex) => {
                                        // Check if this is the last entry in the last date of the last week
                                        const worklogEntries = Object.values(worklogData).flatMap(weekData =>
                                            Object.values(weekData.dates).flatMap(dateData => dateData.entries)
                                        )
                                        const isLastEntry = entry.id === worklogEntries[worklogEntries.length - 1]?.id

                                        return (
                                            <div
                                                key={entry.id}
                                                ref={isLastEntry ? lastWorklogElementRef : null}
                                                className={styles.entryItem}
                                                data-worklog-id={entry.id}
                                            >

                                                <div className={styles.entryMain}>
                                                    {editingWorklogId === entry.id ? (
                                                        <input
                                                            type="text"
                                                            className={`${styles.editableDescription} ${entry.description === '<no description>' ? styles.noDescription : ''}`}
                                                            defaultValue={entry.description === '<no description>' ? '' : entry.description}
                                                            placeholder="No description"
                                                            autoFocus
                                                            onBlur={(e) => {
                                                                const newValue = e.target.value.trim() || ''
                                                                const currentValue = entry.description === '<no description>' ? '' : entry.description
                                                                if (newValue !== currentValue) {
                                                                    handleDescriptionChange(entry.id, newValue)
                                                                }
                                                                setEditingWorklogId(null)
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.target.blur()
                                                                }
                                                                if (e.key === 'Escape') {
                                                                    setEditingWorklogId(null)
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className={`${styles.editableDescription} ${entry.description === '<no description>' ? styles.noDescription : ''}`}
                                                            onClick={() => setEditingWorklogId(entry.id)}
                                                            style={{ cursor: 'text' }}
                                                        >
                                                            {entry.description === '<no description>' ? (
                                                                <span className={styles.placeholder}>No description</span>
                                                            ) : (
                                                                <WorklogDescription description={entry.description} mentionableUsers={mentionableUsernames} />
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className={styles.entryMeta}>
                                                        {(() => {
                                                            const {
                                                                currentProject,
                                                                currentTask
                                                            } = getWorklogProjectAndTask(entry)
                                                            return (
                                                                <ProjectDropdown
                                                                    userProjects={userProjects}
                                                                    userTasks={userTasks}
                                                                    selectedProject={currentProject}
                                                                    selectedTask={currentTask}
                                                                    onProjectSelect={(project) => {
                                                                        handleWorklogProjectChange(entry.id, project, null)
                                                                    }}
                                                                    onTaskSelect={(task) => {
                                                                        const taskProject = userProjects.find(p => p.id === task.project_id)
                                                                        handleWorklogProjectChange(entry.id, taskProject, task)
                                                                    }}
                                                                    onClear={() => {
                                                                        handleWorklogProjectChange(entry.id, null, null)
                                                                    }}
                                                                    trigger={
                                                                        <div className={styles.projectSelector}>
                                                                            <span className={styles.projectDot}>●</span>
                                                                            <span className={styles.projectName}>
                                                                                {currentTask ? `${currentProject?.name || 'Unknown'} > ${currentTask.name}` :
                                                                                    currentProject ? currentProject.name :
                                                                                        entry.project || 'No project'}
                                                                            </span>
                                                                        </div>
                                                                    }
                                                                />
                                                            )
                                                        })()}
                                                    </div>
                                                </div>

                                                <div className={styles.entryControls}>
                                                    <div className={styles.entryTime}>
                                                        <div className={styles.editableTimeRange}>
                                                            <input
                                                                type="text"
                                                                className={styles.timeInput}
                                                                defaultValue={formatTimeForInput(entry.originalWorklog?.start_time)}
                                                                placeholder="09:00"
                                                                onFocus={(e) => e.target.select()}
                                                                onBlur={(e) => {
                                                                    const normalizedTime = normalizeTimeInput(e.target.value)
                                                                    e.target.value = normalizedTime
                                                                    handleTimeChange(entry.id, 'start_time', e.target.value)
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.target.blur()
                                                                    }
                                                                }}
                                                            />
                                                            <span className={styles.timeSeparator}>-</span>
                                                            <input
                                                                type="text"
                                                                className={styles.timeInput}
                                                                defaultValue={formatTimeForInput(entry.originalWorklog?.end_time)}
                                                                placeholder="17:00"
                                                                onFocus={(e) => e.target.select()}
                                                                onBlur={(e) => {
                                                                    const normalizedTime = normalizeTimeInput(e.target.value)
                                                                    e.target.value = normalizedTime
                                                                    handleTimeChange(entry.id, 'end_time', e.target.value)
                                                                }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.target.blur()
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div
                                                            className={styles.calendarIconButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                toggleEntryDatePicker(entry.id)
                                                            }}
                                                            data-entry-date-section
                                                            title="Change date"
                                                        >
                                                            <svg className={styles.calendarIcon} viewBox="0 0 24 24"
                                                                fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="3" y="4" width="18" height="18" rx="2"
                                                                    ry="2"></rect>
                                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                                            </svg>
                                                        </div>

                                                        {entryDatePickers[entry.id] && (
                                                            <div className={styles.entryDatePickerWrapper}
                                                                data-entry-date-picker>
                                                                <DatePickerComponent
                                                                    selectedDate={moment(entry.originalWorklog?.start_time).toDate()}
                                                                    onDateSelect={(date) => handleWorklogDateChange(entry.id, date)}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className={styles.entryDuration}>
                                                        {entry.duration}
                                                    </div>

                                                    <div className={styles.entryActionsWrapper}>
                                                        <button
                                                            className={`${styles.moreButton} moreButton`}
                                                            onClick={(e) => handleMoreButtonClick(entry.id, e)}
                                                        >
                                                            ⋮
                                                        </button>

                                                        {openDropdownId === entry.id && dropdownPosition && createPortal(
                                                            <div
                                                                className={`${styles.entryDropdownPortal} entryDropdown`}
                                                                style={{
                                                                    position: 'fixed',
                                                                    top: dropdownPosition.top,
                                                                    right: dropdownPosition.right,
                                                                }}
                                                            >
                                                                <button
                                                                    className={styles.dropdownItem}
                                                                    onClick={() => handleContinueEntry(entry)}
                                                                >
                                                                    <span className={styles.dropdownIcon}>▶</span>
                                                                    Continue
                                                                </button>
                                                                <button
                                                                    className={styles.dropdownItem}
                                                                    onClick={() => handleSplitEntry(entry)}
                                                                >
                                                                    <span className={styles.dropdownIcon}>✂</span>
                                                                    Split
                                                                </button>
                                                                <button
                                                                    className={styles.dropdownItem}
                                                                    onClick={() => handleDuplicateEntry(entry)}
                                                                >
                                                                    <span className={styles.dropdownIcon}>📋</span>
                                                                    Duplicate
                                                                </button>
                                                                <button
                                                                    className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                                                    onClick={() => handleDeleteEntry(entry.id)}
                                                                >
                                                                    <span className={styles.dropdownIcon}>🗑</span>
                                                                    Delete
                                                                </button>
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

                {/* Infinite scroll loading indicators */}
                {loadingMore && (
                    <div className={styles.loadingMoreIndicator}>
                        <div className={styles.loadingSpinner}></div>
                        <span>Loading more worklogs...</span>
                    </div>
                )}

                {!hasMoreWorklogs && recentWorklogs.length > 0 && (
                    <div className={styles.endOfDataIndicator}>
                        All worklogs loaded
                    </div>
                )}
            </div>

            {/* Project Task Selection Modal */}
            <ProjectTaskSelectionModal
                isOpen={isProjectSelectionModalOpen}
                onClose={handleCloseProjectModal}
                onSelection={handleProjectTaskSelection}
                availableProjects={userProjects}
                availableTasks={userTasks}
                initialSelectedProject={selectedProject}
                initialSelectedTask={selectedTask}
                initialBillable={isBillable}
                showBillableToggle={true}
            />

        </div>
    )
}
